import type { VehicleAvailability, VehicleDocument, VehicleEquipment, VehicleFinance, VehiclePublication, VehicleSpecifications, VehicleText } from './vehicle-template'
import { importedVehicles } from './vehicle-examples'

export type VehicleImageView =
  | 'frontThreeQuarter'
  | 'front'
  | 'rear'
  | 'rearThreeQuarter'
  | 'detail'

export type VehicleImage = {
  src: string
  view: VehicleImageView
  focalPoint: `${number}% ${number}%`
}

export type VehicleEnergy = 'diesel' | 'dieselMildHybrid' | 'petrolMildHybrid' | 'petrol' | 'hybrid' | 'plugInHybrid' | 'electric' | 'lpg' | 'cng'
export type VehicleTransmission = 'automatic' | 'manual'

export type VerifiedVehicleAmount = {
  amountEuro: number
  verifiedAt: string
}

export type VehicleFacts = {
  year?: number
  energy?: VehicleEnergy
  mileageKm?: number
  transmission?: VehicleTransmission
  price?: VerifiedVehicleAmount
  monthlyPayment?: VerifiedVehicleAmount
}

export type Vehicle = {
  id: string
  slug: string
  make: string
  model: string
  images: readonly VehicleImage[]
  facts: VehicleFacts
  accessories: readonly VehicleEquipment[]
  source: 'audit-demo' | 'manual'
  auditedAt?: string
  sourceUrl?: string
  publication: VehiclePublication
  availability?: VehicleAvailability
  offerType: 'sale' | 'rental'
  specifications: VehicleSpecifications
  description?: VehicleText
  commercialNotes?: VehicleText
  documents: readonly VehicleDocument[]
  videoUrl?: string
  finance?: VehicleFinance
  extraDetails?: readonly { id: string; label: VehicleText; value: VehicleText }[]
}

export type RepositoryState<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'empty'; data: readonly [] }
  | { status: 'error'; error: Error }

export interface VehicleRepository {
  list(): Promise<RepositoryState<readonly Vehicle[]>>
  getBySlug(slug: string): Promise<RepositoryState<Vehicle>>
}

/** Source snapshots for local examples, not a live inventory feed. */
export const demoVehicles: readonly Vehicle[] = importedVehicles

/** Starting record for the future editor: unpublished, with no invented commercial facts. */
export function createVehicleDraft(identity: Pick<Vehicle, 'id' | 'slug' | 'make' | 'model'>): Vehicle {
  return {
    ...identity, source: 'manual', publication: 'draft', offerType: 'sale',
    facts: {}, specifications: {}, images: [], accessories: [], documents: [],
  }
}

export class DemoVehicleRepository implements VehicleRepository {
  constructor(private readonly options: { listLimit?: number } = {}) {}

  async list(): Promise<RepositoryState<readonly Vehicle[]>> {
    const published = demoVehicles.filter((vehicle) => vehicle.publication === 'published')
    const vehicles = typeof this.options.listLimit === 'number'
      ? published.slice(0, this.options.listLimit)
      : published
    return vehicles.length ? { status: 'ready', data: vehicles } : { status: 'empty', data: [] }
  }

  async getBySlug(slug: string): Promise<RepositoryState<Vehicle>> {
    const vehicle = demoVehicles.find((item) => item.slug === slug && item.publication === 'published')
    return vehicle ? { status: 'ready', data: vehicle } : { status: 'empty', data: [] }
  }
}

/** Deliberately unimplemented boundary for an authorized ManagerCar integration. */
export type ManagerCarVehicleRepository = VehicleRepository
